import React, { useState, useEffect, useMemo, useCallback } from "react";
import Slider from "react-slick";
import "../../../theme/css/HomePage.css";
import { getActiveBanners, getImagesByBanner } from "../../../lib/api";
import { API_BASE } from "../../../lib/apiHelper";
import { devError } from "../../../utils/devConsole";

const ADAPTIVE_COVER_RATIO_THRESHOLD = 2.2;

const BANNER_HEIGHT =
  "h-[180px] sm:h-[220px] md:h-[260px] lg:h-[280px]";

const BANNER_SKELETON_LAYOUT =
  `banner-preview-skeleton block w-full ${BANNER_HEIGHT}`;

function normalizeUrl(url) {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
  if (trimmed.startsWith("www.")) return "https://" + trimmed;
  if (/^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(trimmed)) return "https://" + trimmed;
  return "";
}

function imgFetchProps(isPriority) {
  if (isPriority) {
    return { loading: "eager", fetchpriority: "high", decoding: "async" };
  }
  return { loading: "lazy", decoding: "async" };
}

function buildBannerImageSrc(filePath) {
  if (!filePath) return "/default.jpg";
  return `${API_BASE.replace(/\/$/, "")}/${filePath.replace(/^\/+/, "").replace(/\\/g, "/")}?${new Date().getTime()}`;
}

export default function Banner() {
  const [items, setItems] = useState([]);
  const [ready, setReady] = useState(false);
  const [ratiosById, setRatiosById] = useState({});

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const active = await getActiveBanners();
        if (!active.length) {
          if (!cancelled) {
            setItems([]);
            setReady(true);
          }
          return;
        }

        const imagesByBanner = await Promise.all(
          active.map(async (b) => {
            const raw = await getImagesByBanner(b.banner_id);
            const filtered = raw.filter(
              (img) => typeof img.Ordine === "number" && img.Ordine >= 1
            );
            return filtered.map((img) => ({
              ...img,
              _bannerNumero: b.Numero ?? 0,
            }));
          })
        );

        const merged = imagesByBanner
          .flat()
          .sort(
            (a, b) =>
              (a._bannerNumero - b._bannerNumero) || (a.Ordine - b.Ordine)
          );

        if (!cancelled) setItems(merged);
      } catch (e) {
        devError("Error fetching home banners:", e);
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const storeRatio = useCallback((id, width, height) => {
    if (!id || !width || !height) return;
    const ratio = width / height;
    setRatiosById((prev) => {
      const existing = prev[id];
      if (existing && Math.abs(existing - ratio) < 0.01) return prev;
      return { ...prev, [id]: ratio };
    });
  }, []);

  const modeFor = useCallback(
    (id) => {
      const ratio = ratiosById[id];
      if (ratio !== undefined && ratio >= ADAPTIVE_COVER_RATIO_THRESHOLD)
        return "cover";
      return "contain-blur";
    },
    [ratiosById]
  );

  const settings = useMemo(
    () => ({
      autoplay: items.length > 1,
      autoplaySpeed: 4500,
      dots: false,
      arrows: false,
      infinite: items.length > 1,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      pauseOnHover: true,
      swipe: items.length > 1,
      draggable: items.length > 1,
      touchMove: items.length > 1,
      adaptiveHeight: false,
    }),
    [items.length]
  );

  const renderSlide = (image, slideIndex) => {
    const imgSrc = buildBannerImageSrc(image.File);
    const shouldOpenInNewTab = image.NuovoTab === 1 && image.Link;
    const finalLink = image.Link ? normalizeUrl(image.Link) : "#";
    const altText = image.Nome || image.File || "Banner";
    const mode = modeFor(image.immagine_id);
    const isLcp = slideIndex === 0;
    const blurProps = imgFetchProps(false);
    const mainProps = imgFetchProps(isLcp);

    const coverBlock = (
      <div className={`relative overflow-hidden bg-white ${BANNER_HEIGHT}`}>
        <img
          className="w-full h-full object-cover object-center"
          alt={altText}
          src={imgSrc}
          onLoad={(e) =>
            storeRatio(
              image.immagine_id,
              e.currentTarget.naturalWidth,
              e.currentTarget.naturalHeight
            )
          }
          {...mainProps}
        />
      </div>
    );

    const containBlurBlock = (
      <div className={`relative overflow-hidden bg-white ${BANNER_HEIGHT}`}>
        <img
          src={imgSrc}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover object-center blur-md scale-110 opacity-45"
          {...blurProps}
        />
        <img
          className="relative z-10 mx-auto h-full w-auto max-w-full object-contain object-center"
          alt={altText}
          src={imgSrc}
          onLoad={(e) =>
            storeRatio(
              image.immagine_id,
              e.currentTarget.naturalWidth,
              e.currentTarget.naturalHeight
            )
          }
          {...mainProps}
        />
      </div>
    );

    const visual = mode === "cover" ? coverBlock : containBlurBlock;

    if (shouldOpenInNewTab) {
      return (
        <a
          href={finalLink}
          target="_blank"
          rel="noopener noreferrer"
          className="block bg-white"
        >
          {visual}
        </a>
      );
    }

    return <div className="bg-white">{visual}</div>;
  };

  if (!ready) {
    return (
      <div
        className="banner banner--loading"
        aria-busy="true"
        aria-label="Caricamento banner"
      >
        <div className={BANNER_SKELETON_LAYOUT} />
      </div>
    );
  }

  if (items.length === 0) {
    return null;
  }

  if (items.length === 1) {
    return (
      <div className="banner">
        <div className="home-banner-slide">{renderSlide(items[0], 0)}</div>
      </div>
    );
  }

  return (
    <div className="banner banner--carousel home-banners-wrap">
      <Slider className="home-banners-slick" {...settings}>
        {items.map((image, slideIndex) => (
          <div key={image.immagine_id} className="home-banner-slide">
            {renderSlide(image, slideIndex)}
          </div>
        ))}
      </Slider>
    </div>
  );
}
